import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { EditEvent, HostEvent } from '../../shared/protocol';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** ISO (UTC) → the YYYY-MM-DDTHH:mm a datetime-local input wants, in LOCAL time. */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

/**
 * Inline "edit the night's details" form for the host dashboard — fixes the
 * common after-the-fact mistakes (wrong time/place/title). Options + invitees are
 * deliberately NOT editable here (changing them would orphan votes / re-mint
 * codes); re-create for those. Same event id, so every invite link keeps working.
 */
export function EditDetails({
  event,
  token,
  onSaved,
  onCancel,
}: {
  event: HostEvent;
  token: string;
  onSaved: (ev: HostEvent) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(event.title);
  const [startsLocal, setStartsLocal] = useState(isoToLocalInput(event.startsAt));
  const [place, setPlace] = useState(event.place);
  const [freq, setFreq] = useState<EditEvent['recurrence']['freq']>(event.recurrence.freq);
  const [weekday, setWeekday] = useState(event.recurrence.weekday ?? 4);
  const [note, setNote] = useState(event.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    if (!place.trim()) {
      setError('Add the room link or code so people can join.');
      return;
    }
    const starts = new Date(startsLocal);
    if (Number.isNaN(starts.getTime())) {
      setError('Pick a valid date and time.');
      return;
    }
    const body: EditEvent = {
      v: 1,
      title: title.trim() || 'Thursday Night',
      startsAt: starts.toISOString(),
      place: place.trim(),
      recurrence: { freq, ...(freq === 'weekly' || freq === 'biweekly' ? { weekday } : {}) },
      note: note.trim() || undefined,
    };
    setSaving(true);
    try {
      const ev = await api.editEvent(event.id, token, body);
      onSaved(ev);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h2>✏️ Edit details</h2>
      <p className="soft">Updates everyone's invite instantly — votes, options, and links stay the same.</p>

      <label>Event title</label>
      <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} />

      <div className="row">
        <div>
          <label>Date &amp; time</label>
          <input
            type="datetime-local"
            value={startsLocal}
            onChange={(e) => setStartsLocal(e.target.value)}
          />
        </div>
        <div>
          <label>Repeats</label>
          <select
            value={freq}
            onChange={(e) => setFreq(e.target.value as EditEvent['recurrence']['freq'])}
          >
            <option value="none">One time only</option>
            <option value="weekly">Every week</option>
            <option value="biweekly">Every other week</option>
            <option value="monthly">Every month</option>
          </select>
        </div>
        {(freq === 'weekly' || freq === 'biweekly') && (
          <div>
            <label>On</label>
            <select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
              {WEEKDAYS.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <label>
        Place <span className="hint">— the VR room link or code people join with</span>
      </label>
      <input type="text" value={place} onChange={(e) => setPlace(e.target.value)} />

      <label>
        Note <span className="hint">(optional)</span>
      </label>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />

      {error && <div className="notice error">{error}</div>}

      <div className="row" style={{ marginTop: 14 }}>
        <button className="btn" onClick={save} disabled={saving}>
          {saving ? <span className="spinner" /> : 'Save changes'}
        </button>
        <button className="btn ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}
